import React, { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Box, Chip } from '@mui/material';
import { DocumentNode } from 'graphql';
import {
  OperationVariables,
  TypedDocumentNode,
  useQuery
} from '@apollo/client';

interface IMultipleAutocompleteFieldProps {
  id?: string;
  label: string;
  query?: {
    query: DocumentNode | TypedDocumentNode<OperationVariables>;
    getter: string;
  };
  options?: Array<Record<string, string | number>>;
  optionLabelKey: string;
  value?: Array<Record<string, string | number>>;
  onChange: (newValue: Array<Record<string, string | number>>) => void;
  touched?: boolean;
  errors?: string;
}

const MultipleAutoCompleteComponent = ({
  id,
  label,
  query,
  options,
  optionLabelKey,
  value,
  onChange,
  touched,
  errors
}: IMultipleAutocompleteFieldProps): JSX.Element => {
  const [results, setResults] = useState<
    Array<Record<string, string | number>>
  >([]);

  if (query) {
    const { data } = useQuery(query.query);
    useEffect(() => {
      if (data) {
        setResults(data[query.getter]);
      }
    }, [data]);
  } else {
    setResults(options as unknown as Array<Record<string, string | number>>);
  }

  const getOptionLabel = (option: Record<string, string | number>): string => {
    if (optionLabelKey === 'role') {
      return option[optionLabelKey]
        .toString()
        .split('_')
        .map(
          (word: string) =>
            `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`
        )
        .join(' ');
    } else {
      return option[optionLabelKey].toString();
    }
  };

  const isDeliveryResponsible = (
    option: Record<string, string | number>
  ): boolean => {
    return getOptionLabel(option).toLowerCase() === 'delivery responsible';
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 500,
          color: '#44474E'
        }}
      >
        {label}
      </Typography>
      <Autocomplete
        id={id}
        sx={{
          width: '100%',
          height: '55px',
          marginTop: '15px',
          '.MuiAutocomplete-clearIndicator': {
            display: 'none'
          }
        }}
        multiple
        value={value}
        options={results}
        getOptionLabel={getOptionLabel}
        onChange={(_event, newValue) => {
          if (newValue) {
            onChange(newValue);
          }
        }}
        renderInput={(params) => <TextField {...params} />}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...otherProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                {...otherProps}
                label={getOptionLabel(option)}
                sx={{
                  backgroundColor: isDeliveryResponsible(option)
                    ? '#c8d2e6'
                    : '#D7E2FF',
                  '& .MuiChip-deleteIcon': {
                    display: isDeliveryResponsible(option)
                      ? 'none'
                      : 'inline-flex'
                  }
                }}
              />
            );
          })
        }
        isOptionEqualToValue={(option, values) =>
          option[optionLabelKey] === values[optionLabelKey]
        }
      />
      <Typography
        sx={{
          color: 'red',
          fontSize: '10px'
        }}
      >
        {touched && errors}
      </Typography>
    </Box>
  );
};

export default MultipleAutoCompleteComponent;
